import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  Row,
  Spinner,
} from "reactstrap";

import CustomTable from "../../../components/CustomTable";
import MetricCard from "../../../components/MetricCard";
import SectionCard, { fieldCardStyle } from "../../../components/SectionCard";
import Status from "../../../components/Status";
import {
  AUTOPILOT_COLORS,
  autopilotCardStyle,
  autopilotDescriptionStyle,
  autopilotEyebrowStyle,
  autopilotHeaderStyle,
  autopilotSecondaryButtonStyle,
  autopilotTitleStyle,
} from "../../../components/autopilot/styles";
import { useCompanies } from "../../../hooks/companies";
import { useAuth } from "../../../hooks/auth";
import api from "../../../services/api";
import TransportadoraDetalheModal from "./TransportadoraDetalheModal";

// Hero no padrão das telas de Auditoria de CTe / Fatura (theme-aware).
const auditHeroHeaderStyle = {
  ...autopilotHeaderStyle,
  padding: "1.65rem 1.65rem 1.45rem",
};
const auditHeroEyebrowStyle = { ...autopilotEyebrowStyle, marginBottom: "0.6rem" };
const auditHeroTitleStyle = {
  ...autopilotTitleStyle,
  margin: "0 0 0.55rem",
  fontSize: "2rem",
  fontWeight: 800,
  lineHeight: 1.15,
};
const auditHeroDescriptionStyle = {
  ...autopilotDescriptionStyle,
  maxWidth: 820,
  marginBottom: 0,
  fontSize: "0.98rem",
  lineHeight: 1.65,
};

const formatDocument = (value) => {
  const digits = String(value || "").replace(/\D/g, "");
  if (digits.length === 11) {
    return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  }
  if (digits.length === 14) {
    return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
  }
  return value || "-";
};

// Caixas/métricas usam os tokens de tema (AUTOPILOT_COLORS / --everlog-*) — nunca
// cor cravada. Containers de seção usam o <SectionCard> compartilhado.
// Container de página com o MESMO fundo do hero (igual às telas de Auditoria).
const containerCardStyle = { ...autopilotCardStyle, background: "var(--everlog-card-hero-bg)" };

const summaryGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: "0.75rem",
  marginBottom: 0,
};

const summaryCardStyle = { ...fieldCardStyle };

const summaryLabelStyle = {
  color: AUTOPILOT_COLORS.muted,
  fontSize: "0.78rem",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  marginBottom: "0.35rem",
};

const summaryValueStyle = {
  color: AUTOPILOT_COLORS.title,
  fontSize: "1.35rem",
  fontWeight: 700,
  lineHeight: 1.1,
};

const workspaceGridStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: "1rem",
  alignItems: "stretch",
};

const workspaceListStyle = {
  display: "flex",
  flex: "1 1 620px",
  minWidth: 0,
};

const workspacePanelStyle = {
  display: "flex",
  flex: "0.85 1 320px",
  minWidth: 0,
};

const workspaceSectionStyle = {
  display: "flex",
  flexDirection: "column",
  height: "100%",
  minHeight: 0,
  width: "100%",
};

const tableRootStyle = {
  flex: "1 1 auto",
  minHeight: 0,
};

const resultSummaryStyle = {
  marginTop: "1.15rem",
  paddingTop: "1rem",
  borderTop: `1px solid ${AUTOPILOT_COLORS.border}`,
};

const resultSummaryHeaderStyle = {
  display: "flex",
  alignItems: "baseline",
  justifyContent: "space-between",
  gap: "0.75rem",
  flexWrap: "wrap",
  marginBottom: "0.75rem",
};

const thStyle = {
  background: AUTOPILOT_COLORS.insetSurface,
  color: AUTOPILOT_COLORS.muted,
  fontSize: "0.82rem",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  borderBottom: `1px solid ${AUTOPILOT_COLORS.border}`,
};

const previewFieldLabelStyle = {
  color: AUTOPILOT_COLORS.muted,
  fontSize: "0.74rem",
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  marginBottom: "0.25rem",
};

const previewFieldValueStyle = {
  color: AUTOPILOT_COLORS.text,
  fontSize: "0.92rem",
  lineHeight: 1.45,
};

const chipRowStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: "0.45rem",
  marginTop: "0.75rem",
};

const filterChipStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: "0.35rem",
  borderRadius: 999,
  padding: "0.38rem 0.72rem",
  background: AUTOPILOT_COLORS.surfacePill,
  border: `1px solid ${AUTOPILOT_COLORS.border}`,
  color: AUTOPILOT_COLORS.text,
  fontSize: "0.78rem",
};

const chipRemoveButtonStyle = {
  background: "transparent",
  border: "none",
  color: AUTOPILOT_COLORS.muted,
  cursor: "pointer",
  lineHeight: 1,
  padding: "0 0 0 0.2rem",
};

// Campos de busca com o MESMO fundo dos campos de data das telas de Indicadores.
const searchInputStyle = {
  background: "var(--everlog-card-surface-field)",
  borderColor: AUTOPILOT_COLORS.border,
  color: AUTOPILOT_COLORS.title,
  boxShadow: "none",
};

// Header do card de filtros (estilo "Recorte da análise").
const filterHeaderStyle = { background: "transparent", border: "none", padding: "1.2rem 1.25rem 0.4rem" };
const filterHeaderTitleStyle = { color: AUTOPILOT_COLORS.title, fontSize: "1.05rem", fontWeight: 700, marginBottom: "0.25rem" };
const filterHeaderDescStyle = { color: AUTOPILOT_COLORS.muted, fontSize: "0.84rem", lineHeight: 1.5 };

const quickFactsGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
  gap: "0.65rem",
  marginBottom: "1rem",
};

const panelActionGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(135px, 1fr))",
  gap: "0.55rem",
  marginTop: "1rem",
};

const selectedHintStyle = {
  color: AUTOPILOT_COLORS.muted,
  fontSize: "0.78rem",
  lineHeight: 1.45,
};

function PreviewField({ label, value }) {
  if (!value) return null;
  return (
    <div style={{ marginBottom: "0.85rem" }}>
      <div style={previewFieldLabelStyle}>{label}</div>
      <div style={previewFieldValueStyle}>{value}</div>
    </div>
  );
}

function QuickFact({ label, value }) {
  const displayValue = value === null || value === undefined || value === "" ? "-" : value;
  return (
    <div style={fieldCardStyle}>
      <div style={summaryLabelStyle}>{label}</div>
      <div style={{ ...summaryValueStyle, fontSize: "1rem", overflowWrap: "anywhere" }}>
        {displayValue}
      </div>
    </div>
  );
}

const Transportadoras = () => {
  const { empresa } = useCompanies();
  const embarcadorId = empresa?.value || null;

  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [data, setData] = useState([]);
  const [filtroCnpjCpf, setFiltroCnpjCpf] = useState("");
  const [filtroRazaoSocial, setFiltroRazaoSocial] = useState("");
  const [filtroFantasia, setFiltroFantasia] = useState("");
  const [filtroStatus, setFiltroStatus] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [previewData, setPreviewData] = useState(null);
  const [detalheId, setDetalheId] = useState(null);
  const [feedback, setFeedback] = useState(null);

  // CRUD (criar/editar/inativar matriz)
  const [formOpen, setFormOpen] = useState(false);
  const [formEdit, setFormEdit] = useState(false);
  const [formId, setFormId] = useState(null);
  const [formFantasia, setFormFantasia] = useState("");
  const [formCnpj, setFormCnpj] = useState("");
  const [formError, setFormError] = useState("");
  const [formSaving, setFormSaving] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);
  // PO1-3102 (mudança de escopo 3): o cadastro global de transportadora e o
  // de-para são só do admin Everlog; o embarcador consulta.
  const { user } = useAuth();
  const podeEditar = user?.setor === "admin";
  const [deleteSaving, setDeleteSaving] = useState(false);

  const flash = (type, text) => {
    setFeedback({ type, text });
    setTimeout(() => setFeedback(null), 4000);
  };

  const carregar = useCallback(
    async (overrides = {}) => {
      try {
        setLoading(true);
        setLoadError("");

        const params = {};
        const cnpjCpf = "cnpjCpf" in overrides ? overrides.cnpjCpf : filtroCnpjCpf;
        const razaoSocial =
          "razaoSocial" in overrides ? overrides.razaoSocial : filtroRazaoSocial;
        const fantasia = "fantasia" in overrides ? overrides.fantasia : filtroFantasia;
        const status = "status" in overrides ? overrides.status : filtroStatus;

        if (embarcadorId) params.embarcadorId = embarcadorId;
        if (cnpjCpf) params.cnpjCpf = cnpjCpf;
        if (razaoSocial) params.razaoSocial = razaoSocial;
        if (fantasia) params.fantasia = fantasia;
        if (status !== "") params.status = status;

        const response = await api.get("/cadastros-gerais/transportadoras", { params });

        const rows = (response.data || []).map((item) => ({
          ...item,
          cnpjCpfFormatado: formatDocument(item.cnpjCpf),
          statusLabel: item.status ? "Ativo" : "Inativo",
        }));

        setData(rows);
        setSelectedId((current) =>
          rows.some((item) => item.id === current) ? current : rows[0]?.id || null
        );
      } catch (error) {
        setData([]);
        setSelectedId(null);
        setPreviewData(null);
        setPreviewError("");
        setLoadError(
          error?.response?.data?.error ||
            "Não foi possível carregar a base de transportadoras para este contexto."
        );
      } finally {
        setLoading(false);
      }
    },
    [embarcadorId, filtroCnpjCpf, filtroFantasia, filtroRazaoSocial, filtroStatus]
  );

  // 1o load e troca de contexto (embarcador): recarrega imediato.
  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embarcadorId]);

  // Filtros (digitacao/status): debounce de 400ms. Antes, cada tecla disparava
  // um request -> respostas fora de ordem e texto embaralhado no input. Pula a
  // carga inicial (ja feita acima). "Aplicar filtros" segue chamando na hora.
  const primeiraCargaRef = useRef(true);
  useEffect(() => {
    if (primeiraCargaRef.current) {
      primeiraCargaRef.current = false;
      return undefined;
    }
    const timer = setTimeout(() => {
      carregar();
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroCnpjCpf, filtroRazaoSocial, filtroFantasia, filtroStatus]);

  useEffect(() => {
    if (!selectedId) {
      setPreviewData(null);
      setPreviewError("");
      return undefined;
    }

    let mounted = true;

    async function carregarPreview() {
      try {
        setPreviewLoading(true);
        setPreviewError("");
        const response = await api.get(`/transportadoras/${selectedId}/detalhe`, {
          params: embarcadorId ? { embarcadorId } : {},
        });
        if (mounted) setPreviewData(response.data || null);
      } catch (error) {
        if (mounted) {
          setPreviewData(null);
          setPreviewError(
            error?.response?.data?.error ||
              "Não foi possível carregar o resumo desta transportadora."
          );
        }
      } finally {
        if (mounted) setPreviewLoading(false);
      }
    }

    carregarPreview();
    return () => {
      mounted = false;
    };
  }, [selectedId, embarcadorId]);

  const limpar = () => {
    setFiltroCnpjCpf("");
    setFiltroRazaoSocial("");
    setFiltroFantasia("");
    setFiltroStatus("");
    carregar({ cnpjCpf: "", razaoSocial: "", fantasia: "", status: "" });
  };

  const selectedRow = useMemo(
    () => data.find((item) => item.id === selectedId) || null,
    [data, selectedId]
  );

  const totais = useMemo(() => {
    const ativos = data.filter((item) => item.status).length;
    return {
      total: data.length,
      ativos,
      inativos: data.length - ativos,
      comFantasia: data.filter((item) => item.fantasia).length,
    };
  }, [data]);

  const activeFilters = useMemo(
    () =>
      [
        filtroCnpjCpf && { key: "cnpjCpf", label: "Documento", value: filtroCnpjCpf },
        filtroRazaoSocial && { key: "razaoSocial", label: "Razão social", value: filtroRazaoSocial },
        filtroFantasia && { key: "fantasia", label: "Apelido", value: filtroFantasia },
        filtroStatus !== "" && {
          key: "status",
          label: "Status",
          value: filtroStatus === "1" ? "Ativo" : "Inativo",
        },
      ].filter(Boolean),
    [filtroCnpjCpf, filtroFantasia, filtroRazaoSocial, filtroStatus]
  );

  const summaryCards = [
    { key: "total", label: "Base carregada", value: totais.total, helper: "Matrizes no recorte atual", status: null, tone: "info", icon: "tim-icons icon-bullet-list-67" },
    { key: "ativos", label: "Ativos", value: totais.ativos, helper: "Disponíveis para operação", status: "1", tone: "success", icon: "tim-icons icon-check-2" },
    { key: "inativos", label: "Inativos", value: totais.inativos, helper: "Exigem revisão cadastral", status: "0", tone: "warning", icon: "tim-icons icon-time-alarm" },
    { key: "fantasia", label: "Com apelido", value: totais.comFantasia, helper: "Indicador simples de completude", status: null, tone: "pink", icon: "tim-icons icon-tag" },
  ];

  const aplicarStatus = (nextStatus) => {
    setFiltroStatus(nextStatus);
    carregar({ status: nextStatus });
  };

  const limparFiltro = (key) => {
    const overrides = {};

    if (key === "cnpjCpf") {
      setFiltroCnpjCpf("");
      overrides.cnpjCpf = "";
    }
    if (key === "razaoSocial") {
      setFiltroRazaoSocial("");
      overrides.razaoSocial = "";
    }
    if (key === "fantasia") {
      setFiltroFantasia("");
      overrides.fantasia = "";
    }
    if (key === "status") {
      setFiltroStatus("");
      overrides.status = "";
    }

    carregar(overrides);
  };

  // ── CRUD handlers ───────────────────────────────────────────────────────────
  const abrirCriar = () => {
    setFormEdit(false);
    setFormId(null);
    setFormFantasia("");
    setFormCnpj("");
    setFormError("");
    setFormOpen(true);
  };

  const abrirEditar = (row) => {
    setFormEdit(true);
    setFormId(row.id);
    setFormFantasia(row.fantasia || row.razaoSocial || "");
    setFormCnpj(row.cnpjCpf || "");
    setFormError("");
    setFormOpen(true);
  };

  const salvarForm = async () => {
    const nome = formFantasia.trim();
    const cnpj = String(formCnpj || "").replace(/\D/g, "");
    if (!nome) return setFormError("Informe o apelido da transportadora.");
    if (cnpj.length !== 11 && cnpj.length !== 14) return setFormError("CNPJ/CPF inválido.");

    try {
      setFormSaving(true);
      setFormError("");
      if (formEdit) {
        await api.put(`/transportadoras/${formId}`, { name: nome, cnpj });
        flash("success", "Transportadora alterada com sucesso!");
      } else {
        await api.post("/transportadoras", { name: nome, cnpj });
        flash("success", "Transportadora criada com sucesso!");
      }
      setFormOpen(false);
      carregar();
    } catch (error) {
      setFormError(error?.response?.data?.error || "Erro ao salvar transportadora.");
    } finally {
      setFormSaving(false);
    }
  };

  const confirmarInativacao = async () => {
    if (!deleteRow) return;
    try {
      setDeleteSaving(true);
      await api.delete(`/transportadoras/${deleteRow.id}`);
      flash("success", "Transportadora inativada com sucesso!");
      setDeleteRow(null);
      carregar();
    } catch (error) {
      flash("danger", error?.response?.data?.error || "Erro ao inativar transportadora.");
    } finally {
      setDeleteSaving(false);
    }
  };

  const exportarCsv = () => {
    const headers = ["Transportadora", "Razão social", "Documento", "Status"];
    const rows = data.map((row) => [
      row.fantasia || row.razaoSocial || "",
      row.razaoSocial || "",
      row.cnpjCpfFormatado || formatDocument(row.cnpjCpf),
      row.statusLabel || "",
    ]);
    const escape = (value) => `"${String(value || "").replace(/"/g, '""')}"`;
    const csv = [headers, ...rows].map((line) => line.map(escape).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "transportadoras.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  const columns = [
    {
      dataField: "transportadora",
      text: "Transportadora",
      headerStyle: thStyle,
      csvExport: false,
      sort: false,
      formatter: (cell, row) => (
        <div style={{ textAlign: "left" }}>
          <div style={{ color: AUTOPILOT_COLORS.title, fontWeight: 600, marginBottom: "0.15rem" }}>
            {row.fantasia || row.razaoSocial || "-"}
          </div>
          <div style={{ color: AUTOPILOT_COLORS.muted, fontSize: "0.78rem", lineHeight: 1.35 }}>
            {row.razaoSocial || "Sem razão social"}
          </div>
        </div>
      ),
    },
    { dataField: "cnpjCpfFormatado", text: "Documento", headerStyle: thStyle },
    {
      dataField: "statusLabel",
      text: "Status",
      headerStyle: thStyle,
      formatter: (cell) => Status({ status: cell, title: cell }),
    },
  ];

  const rowEvents = { onClick: (event, row) => setSelectedId(row.id) };
  const rowStyle = (row) =>
    row.id !== selectedId
      ? {}
      : {
          backgroundColor: AUTOPILOT_COLORS.insetSurfaceActive,
          boxShadow: `inset 0 0 0 1px ${AUTOPILOT_COLORS.accent}`,
        };

  const previewTitle =
    previewData?.fantasia ||
    previewData?.razaoSocial ||
    selectedRow?.fantasia ||
    selectedRow?.razaoSocial ||
    "Nenhuma transportadora selecionada";

  return (
    <div className="content">
      <TransportadoraDetalheModal
        transportadoraId={detalheId}
        embarcadorId={embarcadorId}
        onClose={() => setDetalheId(null)}
      />

      {/* Modal criar/editar */}
      <Modal isOpen={formOpen} toggle={() => setFormOpen(false)} modalClassName="modal-align-top" fade={false}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "1rem 1.5rem 0.75rem", borderBottom: `1px solid ${AUTOPILOT_COLORS.border}` }}>
          <div style={{ fontWeight: 600, fontSize: "0.95rem", color: AUTOPILOT_COLORS.title }}>
            {formEdit ? "Editar transportadora" : "Nova transportadora"}
          </div>
          <button type="button" onClick={() => setFormOpen(false)} style={{ background: "none", border: "none", color: AUTOPILOT_COLORS.muted, fontSize: "1.25rem", cursor: "pointer", lineHeight: 1 }}>×</button>
        </div>
        <ModalBody style={{ padding: "1.25rem 1.5rem" }}>
          {formError && (
            <div style={{ background: AUTOPILOT_COLORS.statusDangerBg, border: `1px solid ${AUTOPILOT_COLORS.statusDangerBorder}`, borderRadius: 8, padding: "0.7rem 0.9rem", color: AUTOPILOT_COLORS.statusDanger, fontSize: "0.84rem", marginBottom: "1rem" }}>
              {formError}
            </div>
          )}
          <Label style={{ fontSize: "0.8rem" }}>Apelido</Label>
          <Input value={formFantasia} placeholder="Apelido (nome curto da transportadora)" onChange={(e) => setFormFantasia(e.target.value)} />
          <Label style={{ fontSize: "0.8rem", marginTop: "0.9rem" }}>CNPJ/CPF</Label>
          <Input value={formCnpj} placeholder="00.000.000/0000-00" onChange={(e) => setFormCnpj(e.target.value)} />
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setFormOpen(false)} disabled={formSaving}>
            Cancelar
          </Button>
          <Button color="info" onClick={salvarForm} disabled={formSaving}>
            {formSaving ? <Spinner size="sm" /> : "Salvar"}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Modal inativar */}
      <Modal isOpen={!!deleteRow} toggle={() => setDeleteRow(null)} modalClassName="modal-align-top" fade={false}>
        <ModalBody style={{ padding: "1.5rem" }}>
          <div style={{ color: AUTOPILOT_COLORS.title, fontSize: "0.95rem", marginBottom: "0.5rem", fontWeight: 600 }}>
            Inativar transportadora
          </div>
          <div style={{ color: AUTOPILOT_COLORS.muted, fontSize: "0.86rem" }}>
            Deseja realmente inativar a transportadora{" "}
            <strong style={{ color: AUTOPILOT_COLORS.title }}>{deleteRow?.fantasia || deleteRow?.razaoSocial}</strong>?
            O registro é inativado (soft delete), preservando o histórico.
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setDeleteRow(null)} disabled={deleteSaving}>
            Cancelar
          </Button>
          <Button color="danger" onClick={confirmarInativacao} disabled={deleteSaving}>
            {deleteSaving ? <Spinner size="sm" /> : "Sim, inativar"}
          </Button>
        </ModalFooter>
      </Modal>

      <Card style={{ ...autopilotCardStyle, background: "var(--everlog-card-hero-bg)" }}>
        <CardHeader style={auditHeroHeaderStyle}>
          <Row className="align-items-start">
            <Col>
              <div style={auditHeroEyebrowStyle}>Cadastro</div>
              <h2 style={auditHeroTitleStyle}>Transportadoras</h2>
              <div style={auditHeroDescriptionStyle}>
                A lista mostra apenas as matrizes; as filiais aparecem no detalhe de cada
                transportadora. Contexto atual: <strong>{empresa?.label || "base geral"}</strong>.
              </div>
            </Col>
            <Col xs="auto">
              <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", justifyContent: "flex-end" }}>
                <Button
                  className="btn-sm"
                  style={autopilotSecondaryButtonStyle}
                  onClick={() => carregar()}
                  disabled={loading}
                >
                  {loading ? <Spinner size="sm" /> : "↺ Atualizar"}
                </Button>
                {podeEditar && (
                  <Button color="info" className="btn-sm" onClick={abrirCriar}>
                    <i className="tim-icons icon-simple-add" /> Nova transportadora
                  </Button>
                )}
              </div>
            </Col>
          </Row>
        </CardHeader>
      </Card>

      <Card style={{ ...containerCardStyle, marginTop: "1.25rem" }}>
        <CardHeader style={filterHeaderStyle}>
          <div style={filterHeaderTitleStyle}>Recorte da carteira</div>
          <div style={filterHeaderDescStyle}>
            Use os filtros para consultar a base de matrizes. O resumo e a tabela abaixo
            sempre refletem o mesmo recorte retornado pela API.
          </div>
        </CardHeader>
        <CardBody style={{ paddingTop: "0.5rem" }}>
          <Row style={{ marginBottom: "0.5rem" }}>
            <Col lg="3" md="6" sm="12">
              <Label style={{ fontSize: "0.8rem" }}>CNPJ/CPF</Label>
              <Input style={searchInputStyle} value={filtroCnpjCpf} onChange={(e) => setFiltroCnpjCpf(e.target.value)} placeholder="Buscar por CNPJ ou CPF..." />
            </Col>
            <Col lg="3" md="6" sm="12">
              <Label style={{ fontSize: "0.8rem" }}>Razão Social</Label>
              <Input style={searchInputStyle} value={filtroRazaoSocial} onChange={(e) => setFiltroRazaoSocial(e.target.value)} placeholder="Buscar por razão social..." />
            </Col>
            <Col lg="3" md="6" sm="12">
              <Label style={{ fontSize: "0.8rem" }}>Apelido</Label>
              <Input style={searchInputStyle} value={filtroFantasia} onChange={(e) => setFiltroFantasia(e.target.value)} placeholder="Buscar por apelido..." />
            </Col>
            <Col lg="3" md="6" sm="12">
              <Label style={{ fontSize: "0.8rem" }}>Status</Label>
              <Input style={searchInputStyle} type="select" value={filtroStatus} onChange={(e) => setFiltroStatus(e.target.value)}>
                <option value="">Todos</option>
                <option value="1">Ativo</option>
                <option value="0">Inativo</option>
              </Input>
            </Col>
          </Row>

          {activeFilters.length > 0 && (
            <div style={chipRowStyle}>
              {activeFilters.map((item) => (
                <div key={item.key} style={filterChipStyle}>
                  <strong>{item.label}:</strong>
                  <span>{item.value}</span>
                  <button
                    type="button"
                    aria-label={`Remover filtro ${item.label}`}
                    onClick={() => limparFiltro(item.key)}
                    style={chipRemoveButtonStyle}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.75rem", marginTop: "1rem", flexWrap: "wrap" }}>
            <Button color="info" onClick={() => carregar()}>
              Aplicar filtros
            </Button>
            <Button color="secondary" outline onClick={limpar}>
              Limpar recorte
            </Button>
          </div>

          {feedback && (
            <div
              style={{
                marginTop: "1rem",
                background: feedback.type === "success" ? AUTOPILOT_COLORS.statusSuccessBg : AUTOPILOT_COLORS.statusDangerBg,
                border: `1px solid ${feedback.type === "success" ? AUTOPILOT_COLORS.statusSuccessBorder : AUTOPILOT_COLORS.statusDangerBorder}`,
                borderRadius: 12,
                padding: "0.9rem 1rem",
                color: feedback.type === "success" ? AUTOPILOT_COLORS.statusSuccess : AUTOPILOT_COLORS.statusDanger,
                fontSize: "0.84rem",
              }}
            >
              {feedback.text}
            </div>
          )}

          {loadError && (
            <div style={{ marginTop: "1rem", background: AUTOPILOT_COLORS.statusDangerBg, border: `1px solid ${AUTOPILOT_COLORS.statusDangerBorder}`, borderRadius: 12, padding: "0.9rem 1rem", color: AUTOPILOT_COLORS.statusDanger, fontSize: "0.84rem" }}>
              {loadError}
            </div>
          )}

          <div style={resultSummaryStyle}>
            <div style={resultSummaryHeaderStyle}>
              <div>
                <div style={filterHeaderTitleStyle}>Resumo do resultado atual</div>
                <div style={filterHeaderDescStyle}>
                  Os cards de status também refinam o recorte da listagem.
                </div>
              </div>
              <div style={selectedHintStyle}>
                {loading ? "Atualizando..." : `${totais.total} matriz(es) carregada(s)`}
              </div>
            </div>
            <div style={summaryGridStyle}>
              {summaryCards.map((card) => {
                const isFilterCard = card.status !== null;
                const isActive = isFilterCard && filtroStatus === card.status;
                const onClick = isFilterCard
                  ? () => {
                      const nextStatus = filtroStatus === card.status ? "" : card.status;
                      aplicarStatus(nextStatus);
                    }
                  : undefined;

                return (
                  <MetricCard
                    key={card.key}
                    label={card.label}
                    value={card.value}
                    helper={card.helper}
                    iconClassName={card.icon}
                    tone={card.tone}
                    onClick={onClick}
                    active={isActive}
                  />
                );
              })}
            </div>
          </div>
        </CardBody>
      </Card>

      <Card style={{ ...containerCardStyle, marginTop: "1.25rem" }}>
        <CardBody>
          <div style={workspaceGridStyle}>
            <div style={workspaceListStyle}>
              <SectionCard
                title="Carteira carregada"
                description="Clique em uma linha para revisar o resumo e acionar detalhes, edição ou inativação no painel lateral."
                style={workspaceSectionStyle}
                action={
                  <Button
                    className="btn-sm"
                    style={autopilotSecondaryButtonStyle}
                    onClick={exportarCsv}
                    disabled={loading || data.length === 0}
                  >
                    Exportar CSV
                  </Button>
                }
              >
                <CustomTable
                  data={data}
                  columns={columns}
                  fileName="transportadoras"
                  tableName="Transportadoras"
                  loading={loading}
                  sizePerPage={10}
                  clearSearchLabel="Limpar busca"
                  searchPlaceholder="Buscar na tabela..."
                  tableClasses="text-left"
                  rowEvents={rowEvents}
                  rowStyle={rowStyle}
                  showToolbar={false}
                  fillHeight
                  themedPagination
                  rootStyle={tableRootStyle}
                />
              </SectionCard>
            </div>

            <div style={workspacePanelStyle}>
              <SectionCard
                title="Resumo da transportadora"
                description="Leitura rápida da matriz selecionada e ações relacionadas ao cadastro."
                style={workspaceSectionStyle}
              >
                {!selectedId && !loading && (
                  <div style={{ color: AUTOPILOT_COLORS.muted, fontSize: "0.84rem" }}>
                    Selecione uma linha da carteira para ver o resumo e as ações disponíveis.
                  </div>
                )}

                {selectedId && (
                  <>
                    <div style={{ ...summaryCardStyle, marginBottom: "1rem", borderColor: AUTOPILOT_COLORS.accent }}>
                      <div style={summaryLabelStyle}>Em foco</div>
                      <div style={{ ...summaryValueStyle, fontSize: "1.1rem", marginBottom: "0.35rem" }}>
                        {previewTitle}
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
                        {selectedRow?.statusLabel && (
                          <Badge color={selectedRow.status ? "success" : "warning"} pill>
                            {selectedRow.statusLabel}
                          </Badge>
                        )}
                        <Badge color="secondary" pill>
                          {selectedRow?.cnpjCpfFormatado || formatDocument(previewData?.cnpjCpf)}
                        </Badge>
                      </div>
                    </div>

                    {previewLoading ? (
                      <div style={{ textAlign: "center", padding: "1.5rem 0" }}>
                        <Spinner size="sm" color="info" />
                      </div>
                    ) : previewError ? (
                      <div style={{ background: AUTOPILOT_COLORS.statusDangerBg, border: `1px solid ${AUTOPILOT_COLORS.statusDangerBorder}`, borderRadius: 12, padding: "0.9rem 1rem", color: AUTOPILOT_COLORS.statusDanger, fontSize: "0.84rem", marginBottom: "1rem" }}>
                        {previewError}
                      </div>
                    ) : (
                      <>
                        <div style={quickFactsGridStyle}>
                          <QuickFact
                            label="Documento"
                            value={selectedRow?.cnpjCpfFormatado || formatDocument(previewData?.cnpjCpf)}
                          />
                          <QuickFact
                            label="Filiais"
                            value={previewData?.filiais ? previewData.filiais.length : "-"}
                          />
                        </div>
                        <PreviewField label="Razão social" value={previewData?.razaoSocial || selectedRow?.razaoSocial} />
                        <PreviewField label="Apelido" value={previewData?.fantasia || selectedRow?.fantasia} />
                        <PreviewField
                          label="Endereço resumido"
                          value={[previewData?.logradouro, previewData?.numero, previewData?.complemento].filter(Boolean).join(", ")}
                        />
                        <PreviewField
                          label="Bairro / Cidade / UF"
                          value={[previewData?.bairro, previewData?.cidade, previewData?.uf].filter(Boolean).join(" - ")}
                        />
                        <PreviewField label="Observação" value={previewData?.observacao} />
                      </>
                    )}

                    <div style={panelActionGridStyle}>
                      <Button color="info" onClick={() => setDetalheId(selectedId)}>
                        Detalhes e filiais
                      </Button>
                      {podeEditar && (
                        <Button
                          color="secondary"
                          outline
                          onClick={() => selectedRow && abrirEditar(selectedRow)}
                          disabled={!selectedRow}
                        >
                          Editar
                        </Button>
                      )}
                      {podeEditar && (
                        <Button
                          color="danger"
                          outline
                          onClick={() => selectedRow && setDeleteRow(selectedRow)}
                          disabled={!selectedRow}
                        >
                          Inativar
                        </Button>
                      )}
                    </div>
                  </>
                )}
              </SectionCard>
            </div>
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

export default Transportadoras;
